import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { FundPerformance, formatChartSlotsForDisplay } from "@ui-kit/components/index.es";
import { actions as fundcorpActions, selectors as fundcorpSelectors } from "reducers/fundcorp";
import { getDefaultShareAccountId, getPerformanceTooltip } from "util/fundcorp";
import * as i18n from "util/i18n";

const getLabels = (key) => {
    const labels = {
        shareValue: i18n.get("fundcorp.performance.kpi.shareValue"),
        shareQuantity: i18n.get("fundcorp.performance.kpi.shareQuantity"),
        balanceValue: i18n.get("fundcorp.performance.kpi.balanceValue"),
        balanceValueMobile: i18n.get("fundcorp.performance.kpi.balanceValueMobile"),
        DAILY: i18n.get("fundcorp.performance.tabs.daily"),
        WEEKLY: i18n.get("fundcorp.performance.tabs.weekly"),
        MONTHLY: i18n.get("fundcorp.performance.tabs.monthly"),
        RANGE: i18n.get("fundcorp.performance.tabs.betweenDates"),
    };
    return labels[key] || key;
};

const fmt = (d) => (d instanceof Date ? d : new Date(d)).toISOString().slice(0, 10);

const fmtDisplay = (d) => {
    const date = d instanceof Date ? d : new Date(d);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

const parseDateOnly = (value) => {
    if (!value) {
        return null;
    }
    if (value instanceof Date) {
        return value;
    }
    const [year, month, day] = String(value)
        .slice(0, 10)
        .split("-")
        .map(Number);
    if (year && month && day) {
        return new Date(year, month - 1, day);
    }
    return new Date(value);
};

const getLast7DaysRange = () => {
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - 6);
    return { dateFrom: fmt(from), dateTo: fmt(today) };
};

const toDateRangeTuple = (dateFrom, dateTo) => [parseDateOnly(dateFrom), parseDateOnly(dateTo)];

const getDateRangeForPeriod = (period) => {
    if (period !== "RANGE") {
        return {};
    }
    return getLast7DaysRange();
};

const getDefaultDatesRange = () => {
    const { dateFrom, dateTo } = getLast7DaysRange();
    return toDateRangeTuple(dateFrom, dateTo);
};

const resolvePeriodKpiLabel = (period, rangeDateFrom, rangeDateTo) => {
    switch (period) {
        case "DAILY":
            return i18n.get("fundcorp.performance.kpi.period.daily");
        case "WEEKLY":
            return i18n.get("fundcorp.performance.kpi.period.weekly");
        case "MONTHLY":
            return i18n.get("fundcorp.performance.kpi.period.monthly");
        case "RANGE":
            if (rangeDateFrom && rangeDateTo) {
                return `${fmtDisplay(rangeDateFrom)} - ${fmtDisplay(rangeDateTo)}`;
            }
            return i18n.get("fundcorp.performance.tabs.betweenDates");
        default:
            return "";
    }
};

const buildFundOptions = (positionsByShareAccount, shareAccountId) =>
    (positionsByShareAccount || [])
        .flatMap((group) => group.positionList || [])
        .filter((pos) => String(pos.cuotapartistaNumero) === String(shareAccountId))
        .map((pos) => ({
            value: String(pos.fondoNumero),
            label: pos.fondoNombre || String(pos.fondoNumero),
            abreviatura: pos.tipoVCPAbreviatura,
        }));

const getDefaultFundSelection = (fundOptions, preload) => {
    if (preload?.fundId) {
        const preloadOption = fundOptions.find((opt) => String(opt.value) === String(preload.fundId));
        if (preloadOption) {
            return {
                fundId: preloadOption.value,
                abreviatura: preload.abreviatura || preloadOption.abreviatura,
            };
        }
    }
    return { fundId: null, abreviatura: null };
};

const shouldAutoFetchOnLoad = (preload) => Boolean(preload?.fundId && preload?.abreviatura);

const buildSelectionSnapshot = (shareAccountId, fundId) => ({
    shareAccountId: shareAccountId ? String(shareAccountId) : null,
    fundId: fundId ? String(fundId) : null,
});

const isSameSelection = (a, b) => a.shareAccountId === b.shareAccountId && a.fundId === b.fundId;

const toIsoDateOnly = (date) => {
    if (!date) {
        return null;
    }
    const d = date instanceof Date ? date : new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const isSameDateRange = (rangeA, rangeB) => {
    const fromA = toIsoDateOnly(rangeA?.[0]);
    const toA = toIsoDateOnly(rangeA?.[1]);
    const fromB = toIsoDateOnly(rangeB?.[0]);
    const toB = toIsoDateOnly(rangeB?.[1]);
    if (!fromA || !toA || !fromB || !toB) {
        return false;
    }
    return fromA === fromB && toA === toB;
};

const FundPerformanceTab = ({
    preload,
    shareAccounts,
    positionsByShareAccount,
    performanceChartSlots,
    performanceChartSlotStyle,
    performanceLastBusinessDay,
    performanceKpis,
    isFetchingPerformance,
    fetchPerformance,
    clearPerformance,
}) => {
    const shareAccountOptions = (shareAccounts || [])
        .sort((a, b) => a.description.localeCompare(b.description))
        .map((sa) => ({ value: String(sa.id), label: `${sa.id} ${sa.description}` }));

    const initialShareAccountId = getDefaultShareAccountId(shareAccounts, preload?.shareAccountId);

    const [selectedShareAccountId, setSelectedShareAccountId] = useState(initialShareAccountId);

    const initialFundOptions = buildFundOptions(positionsByShareAccount, initialShareAccountId);
    const initialFundSelection = getDefaultFundSelection(initialFundOptions, preload);

    const [fundOptions, setFundOptions] = useState(initialFundOptions);
    const [selectedFundId, setSelectedFundId] = useState(initialFundSelection.fundId);
    const [selectedAbreviatura, setSelectedAbreviatura] = useState(initialFundSelection.abreviatura);

    const [selectedPeriod, setSelectedPeriod] = useState(preload?.viewType || "DAILY");
    const [activeRangeDates, setActiveRangeDates] = useState(getDefaultDatesRange());
    const [appliedSelection, setAppliedSelection] = useState(() =>
        buildSelectionSnapshot(initialShareAccountId, initialFundSelection.fundId),
    );

    const selectedShareAccount = (shareAccounts || []).find((sa) => String(sa.id) === String(selectedShareAccountId));
    const verifyCode = selectedShareAccount?.verifyCode || preload?.verifyCode;

    const hasFetchedOnce = useRef(false);
    const [hasChartData, setHasChartData] = useState(false);

    useEffect(() => {
        clearPerformance();
        hasFetchedOnce.current = false;
        setSelectedPeriod(preload?.viewType || "DAILY");
        setActiveRangeDates(getDefaultDatesRange());
        setAppliedSelection(buildSelectionSnapshot(null, null));
        if (!preload?.fundId) {
            setSelectedFundId(null);
            setSelectedAbreviatura(null);
        }
        setHasChartData(false);
        return () => clearPerformance();
    }, [clearPerformance, preload?.fundId, preload?.viewType]);

    useEffect(() => {
        if ((performanceChartSlots || []).length > 0 && !isFetchingPerformance) {
            setHasChartData(true);
        }
    }, [performanceChartSlots, isFetchingPerformance]);

    const currentSelection = useMemo(() => buildSelectionSnapshot(selectedShareAccountId, selectedFundId), [
        selectedShareAccountId,
        selectedFundId,
    ]);

    const isFilterDisabled =
        !selectedShareAccountId ||
        !selectedFundId ||
        !selectedAbreviatura ||
        isSameSelection(currentSelection, appliedSelection);

    const requestPerformance = (period, dateFrom, dateTo) => {
        if (!selectedShareAccountId || !selectedFundId || !selectedAbreviatura || !verifyCode) {
            return;
        }
        const range = period === "RANGE" ? { dateFrom, dateTo } : {};
        fetchPerformance(
            selectedShareAccountId,
            selectedFundId,
            selectedAbreviatura,
            period,
            verifyCode,
            range.dateFrom,
            range.dateTo,
        );
        hasFetchedOnce.current = true;
    };

    useEffect(() => {
        if (selectedShareAccountId || preload?.shareAccountId) {
            return;
        }
        const defaultShareAccountId = getDefaultShareAccountId(shareAccounts, preload?.shareAccountId);
        if (defaultShareAccountId) {
            setSelectedShareAccountId(defaultShareAccountId);
        }
    }, [shareAccounts, preload?.shareAccountId, selectedShareAccountId]);

    useEffect(() => {
        if (!selectedShareAccountId) {
            setFundOptions([]);
            setSelectedFundId(null);
            setSelectedAbreviatura(null);
            return;
        }
        const opts = buildFundOptions(positionsByShareAccount, selectedShareAccountId);
        setFundOptions(opts);

        const preloadForAccount =
            preload?.fundId && String(preload.shareAccountId) === String(selectedShareAccountId) ? preload : null;

        setSelectedFundId((prevFundId) => {
            const { fundId, abreviatura } = getDefaultFundSelection(opts, preloadForAccount);
            if (fundId) {
                setSelectedAbreviatura(abreviatura);
                return fundId;
            }
            if (prevFundId && opts.some((opt) => opt.value === String(prevFundId))) {
                const matched = opts.find((opt) => opt.value === String(prevFundId));
                setSelectedAbreviatura(matched?.abreviatura || null);
                return prevFundId;
            }
            setSelectedAbreviatura(null);
            return null;
        });
    }, [positionsByShareAccount, selectedShareAccountId, preload]);

    useEffect(() => {
        hasFetchedOnce.current = false;
    }, [selectedShareAccountId]);

    useEffect(() => {
        if (!selectedShareAccountId || !selectedFundId || !selectedAbreviatura || !verifyCode) {
            return;
        }
        if (hasFetchedOnce.current) {
            return;
        }
        if (!shouldAutoFetchOnLoad(preload)) {
            return;
        }
        const { dateFrom, dateTo } = getDateRangeForPeriod(selectedPeriod);
        if (selectedPeriod === "RANGE") {
            setActiveRangeDates(toDateRangeTuple(dateFrom, dateTo));
        }
        requestPerformance(selectedPeriod, dateFrom, dateTo);
        setAppliedSelection(buildSelectionSnapshot(selectedShareAccountId, selectedFundId));
    }, [selectedShareAccountId, selectedFundId, selectedAbreviatura, verifyCode]);

    const handleFilter = () => {
        if (isFilterDisabled) {
            return;
        }
        const { dateFrom, dateTo } = getDateRangeForPeriod(selectedPeriod);
        if (selectedPeriod === "RANGE") {
            setActiveRangeDates(toDateRangeTuple(dateFrom, dateTo));
        }
        requestPerformance(selectedPeriod, dateFrom, dateTo);
        setAppliedSelection(currentSelection);
    };

    const handleChangePeriod = (period) => {
        if (period === selectedPeriod) {
            return;
        }
        setSelectedPeriod(period);
        const { dateFrom, dateTo } = getDateRangeForPeriod(period);
        if (period === "RANGE") {
            setActiveRangeDates(toDateRangeTuple(dateFrom, dateTo));
        }
        if (!hasFetchedOnce.current) {
            return;
        }
        if (!selectedShareAccountId || !selectedFundId || !selectedAbreviatura || !verifyCode) {
            return;
        }
        requestPerformance(period, dateFrom, dateTo);
    };

    const handleChangeFund = ({ shareAccount, fund }) => {
        const newShareAccountId = shareAccount ? String(shareAccount) : null;
        const newFundId = fund ? String(fund) : null;

        if (!newShareAccountId) {
            setSelectedShareAccountId(null);
            setFundOptions([]);
            setSelectedFundId(null);
            setSelectedAbreviatura(null);
            return;
        }

        if (newShareAccountId === selectedShareAccountId) {
            if (!newFundId) {
                setSelectedFundId(null);
                setSelectedAbreviatura(null);
                return;
            }
            const fundOpt = fundOptions.find((f) => f.value === newFundId);
            setSelectedFundId(newFundId);
            setSelectedAbreviatura(fundOpt?.abreviatura || null);
            return;
        }

        const opts = buildFundOptions(positionsByShareAccount, newShareAccountId);
        setSelectedShareAccountId(newShareAccountId);
        setFundOptions(opts);
        setSelectedFundId(null);
        setSelectedAbreviatura(null);
    };

    const handleSubmitBetweenDates = (values) => {
        const [dateFrom, dateTo] = values?.dateRange || [null, null];
        if (!dateFrom || !dateTo) {
            return;
        }
        if (isSameDateRange([dateFrom, dateTo], activeRangeDates)) {
            return;
        }
        setSelectedPeriod("RANGE");
        setActiveRangeDates([dateFrom, dateTo]);
        requestPerformance("RANGE", fmt(dateFrom), fmt(dateTo));
    };

    const hasPerformanceData = (performanceChartSlots || []).length > 0;
    const chartLoading = isFetchingPerformance && hasChartData;
    const chartData = hasPerformanceData
        ? formatChartSlotsForDisplay(performanceChartSlots, performanceChartSlotStyle)
        : [];
    const currency = performanceKpis?.currency || "ARS";
    const rangeLabelFrom = selectedPeriod === "RANGE" ? activeRangeDates[0] : null;
    const rangeLabelTo = selectedPeriod === "RANGE" ? activeRangeDates[1] : null;

    return (
        <div style={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
            {shareAccountOptions.length > 0 && (
                <FundPerformance
                    data={chartData}
                    shareAccountOptions={shareAccountOptions}
                    fundOptions={fundOptions}
                    defaultFund={selectedFundId || ""}
                    defaultShareAccount={selectedShareAccountId || ""}
                    shareAccountPlaceholder={i18n.get("fundcorp.list.shareAccount.placeholder")}
                    fundPlaceholder={i18n.get("fundcorp.list.fund.placeholder")}
                    filterButtonText={i18n.get("fundcorp.performance.filter.button")}
                    rangeMaxDate={performanceLastBusinessDay}
                    defaultPeriod={selectedPeriod}
                    defaultDatesRange={selectedPeriod === "RANGE" ? activeRangeDates : getDefaultDatesRange()}
                    appliedDatesRange={activeRangeDates}
                    currency={currency}
                    periodKpiLabel={resolvePeriodKpiLabel(selectedPeriod, rangeLabelFrom, rangeLabelTo)}
                    periodValue={Number(performanceKpis?.periodAmountARS) || 0}
                    periodPercentage={Number(performanceKpis?.percentageVariation) || 0}
                    chartLoading={chartLoading}
                    balanceValue={Number(performanceKpis?.balanceValue) || 0}
                    fundBalancePercentage={Number(performanceKpis?.lastBusinessDayPercentage) || 0}
                    balanceTooltip={getPerformanceTooltip()}
                    getLabels={getLabels}
                    onChangePeriod={handleChangePeriod}
                    onChangeFund={handleChangeFund}
                    onSubmitBetweenDates={handleSubmitBetweenDates}
                    onFilter={handleFilter}
                    filterDisabled={isFilterDisabled}
                    emptyStateText={
                        selectedShareAccountId && fundOptions.length === 0
                            ? i18n.get("fundcorp.performance.empty")
                            : undefined
                    }
                />
            )}
        </div>
    );
};

FundPerformanceTab.propTypes = {
    preload: PropTypes.shape({
        shareAccountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        fundId: PropTypes.string,
        abreviatura: PropTypes.string,
        verifyCode: PropTypes.string,
        viewType: PropTypes.string,
    }),
    shareAccounts: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            description: PropTypes.string,
            verifyCode: PropTypes.string,
        }),
    ),
    positionsByShareAccount: PropTypes.arrayOf(
        PropTypes.shape({
            positionList: PropTypes.arrayOf(
                PropTypes.shape({
                    cuotapartistaNumero: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                    fondoNumero: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                    tipoVCPAbreviatura: PropTypes.string,
                    fondoNombre: PropTypes.string,
                }),
            ),
        }),
    ),
    performanceChartSlots: PropTypes.arrayOf(
        PropTypes.shape({
            bucketEndIso: PropTypes.string,
            amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
    ),
    performanceChartSlotStyle: PropTypes.string,
    performanceLastBusinessDay: PropTypes.string,
    performanceKpis: PropTypes.shape({
        currency: PropTypes.string,
        periodAmountARS: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        percentageVariation: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        balanceValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        lastBusinessDayPercentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    isFetchingPerformance: PropTypes.bool,
    fetchPerformance: PropTypes.func.isRequired,
    clearPerformance: PropTypes.func.isRequired,
};

FundPerformanceTab.defaultProps = {
    preload: null,
    shareAccounts: [],
    positionsByShareAccount: [],
    performanceChartSlots: [],
    performanceChartSlotStyle: "DAILY",
    performanceLastBusinessDay: null,
    performanceKpis: {},
    isFetchingPerformance: false,
};

const mapStateToProps = (state) => ({
    shareAccounts: fundcorpSelectors.getShareAccounts(state),
    positionsByShareAccount: fundcorpSelectors.getPositionsByShareAccount(state),
    performanceChartSlots: fundcorpSelectors.getPerformanceChartSlots(state),
    performanceChartSlotStyle: fundcorpSelectors.getPerformanceChartSlotStyle(state),
    performanceLastBusinessDay: fundcorpSelectors.getPerformanceLastBusinessDay(state),
    performanceKpis: fundcorpSelectors.getPerformanceKpis(state),
    isFetchingPerformance: fundcorpSelectors.isFetchingPerformance(state),
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            fetchPerformance: fundcorpActions.fetchPerformance,
            clearPerformance: fundcorpActions.clearPerformance,
        },
        dispatch,
    );

export default connect(mapStateToProps, mapDispatchToProps)(FundPerformanceTab);
